import React from "react";
import { useAppSelector } from "features/config/hooks";
import { useNavigate } from "react-router-dom";
import { selectBatchProductReviews } from "../slices/batch-product-reviews.slice";

// Add the missing interface types directly here
export interface StockOrderProductInfoProps {
  onRatingClick?: (productId: string) => void;
}

export interface PreviewProductDataProps {
  idx: number;
  productData: any; // Your existing product data type
  setProductData: (data: any) => void; // Your existing TableDataChange type
  statusId: number;
  onRatingClick?: (productId: string) => void;
}

interface BatchInlineRatingProps {
  productId: string;
  onNavigate?: () => void;
}

interface BatchProductRatingDisplayProps {
  productId: string;
  productName?: string;
  showProductInfo?: boolean;
  size?: "sm" | "md" | "lg";
  onNavigate?: () => void;
}

interface StarDisplayProps {
  rating: number;
  size: "sm" | "md" | "lg";
}

interface BatchProductRatingsListProps {
  products: Array<{
    id: string;
    product_id: string;
    product_name: string;
  }>;
  onNavigate?: () => void;
}

// Star display component
const StarDisplay: React.FC<StarDisplayProps> = ({ rating, size }) => {
  const sizeClasses = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${sizeClasses[size]} ${
            star <= Math.round(rating) ? "text-yellow-400" : "text-gray-300"
          } group-hover:scale-110 transition-transform duration-200`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// Batch inline rating (replaces InlineRating)
export const BatchInlineRating: React.FC<BatchInlineRatingProps> = ({
  productId,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const batchReviewsState = useAppSelector(selectBatchProductReviews);

  const productData = batchReviewsState.data?.[productId];
  const loading = batchReviewsState.status === 1; // inProgress
  const rating = productData?.average_rating || 0;

  const handleRatingClick = () => {
    if (onNavigate) {
      onNavigate();
      return;
    }
    navigate(`/admin/product-reviews/${productId}`);
  };

  if (loading) {
    return (
      <span className="inline-block animate-pulse bg-gray-200 rounded text-xs px-1 h-4 w-8 ml-2"></span>
    );
  }

  if (rating === 0) {
    return <span className="text-xs text-gray-400 ml-2">No rating</span>;
  }

  return (
    <button
      onClick={handleRatingClick}
      className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full hover:bg-yellow-200 transition-colors duration-200 cursor-pointer"
      title="Click to view reviews"
    >
      ⭐ {rating.toFixed(1)}
    </button>
  );
};

// Batch product rating display (replaces ProductRatingDisplay)
export const BatchProductRatingDisplay: React.FC<
  BatchProductRatingDisplayProps
> = ({
  productId,
  productName,
  showProductInfo = false,
  size = "md",
  onNavigate,
}) => {
  const navigate = useNavigate();
  const batchReviewsState = useAppSelector(selectBatchProductReviews);

  const productData = batchReviewsState.data?.[productId];
  const loading = batchReviewsState.status === 1; // inProgress
  const error = batchReviewsState.status === 3; // fail
  const rating = productData?.average_rating || 0;

  const handleRatingClick = () => {
    if (onNavigate) {
      onNavigate();
      return;
    }
    navigate(`/admin/product-reviews/${productId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-pulse bg-gray-200 rounded h-4 w-12"></div>
        <div className="animate-pulse bg-gray-200 rounded h-3 w-16"></div>
      </div>
    );
  }

  if (error || rating === 0) {
    return (
      <div className="flex items-center space-x-2">
        {showProductInfo && (
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900 text-sm">
              {productId}
            </span>
            {productName && (
              <span className="text-gray-600 text-sm">{productName}</span>
            )}
          </div>
        )}
        <span className="text-gray-400 text-sm">No reviews</span>
      </div>
    );
  }

  const textSizes = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  return (
    <div className="flex items-center space-x-3">
      {showProductInfo && (
        <div className="flex items-center space-x-2">
          <span className="font-medium text-gray-900 text-sm">{productId}</span>
          {productName && (
            <span className="text-gray-600 text-sm">{productName}</span>
          )}
        </div>
      )}

      <button
        onClick={handleRatingClick}
        className={`flex items-center space-x-2 ${textSizes[size]} text-blue-600 hover:text-blue-800 transition-colors duration-200 group cursor-pointer`}
        title="View product reviews"
      >
        <span className="font-medium">{rating.toFixed(1)}</span>
        <StarDisplay rating={rating} size={size} />
        <span className="text-xs text-gray-500 group-hover:text-gray-700">
          ({rating > 0 ? "View reviews" : "No reviews"})
        </span>
      </button>
    </div>
  );
};

// Batch product ratings list (replaces ProductRatingsList)
export const BatchProductRatingsList: React.FC<
  BatchProductRatingsListProps
> = ({ products, onNavigate }) => {
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3 text-gray-900">
        Product Ratings
      </h3>
      <div className="space-y-3">
        {products.map((product) => (
          <div
            key={product.id}
            className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0"
          >
            <BatchProductRatingDisplay
              productId={product.product_id}
              productName={product.product_name}
              showProductInfo={true}
              onNavigate={onNavigate}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Keep backward compatibility by exporting these names too
export const InlineRating = BatchInlineRating;
export const ProductRatingDisplay = BatchProductRatingDisplay;
export const ProductRatingsList = BatchProductRatingsList;
